import React, { useEffect, useState } from 'react';
import apiService from '../../services/apiService';
import { DSCard } from '../../design-system/primitives';
import './mergeAccounts.css';

// Merges already done.
//
// Carried over from the old Account Merge tab, which was the only view of
// account_merge_log anywhere in the app. A merge cannot be undone from the app,
// so the log is the only way to answer "what happened to this person's other
// account" - deleting the page it lived on would have taken that with it.
//
// Rebuilt as rows rather than a five-column table: at phone width that table
// put Date, Source, Target, Merged By and Details into columns narrow enough
// that the emails wrapped one character at a time.

function MergeHistory() {
  const [merges, setMerges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiService.getMergeHistory()
      .then((res) => { if (!cancelled) setMerges(res.data || []); })
      .catch(() => { if (!cancelled) setFailed(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;

  return (
    <DSCard
      cornerVariant="lg"
      className="merge-history"
      frame="bars"
      headerTone="strong"
      density="compact"
      flush
      title="Merges already done"
    >
      {failed && <p className="merge-history__empty">Could not load the merge history.</p>}
      {!failed && merges.length === 0 && (
        <p className="merge-history__empty">No accounts have been merged yet.</p>
      )}
      {!failed && merges.length > 0 && (
        <ul className="merge-history__list">
          {merges.map((merge) => (
            <li key={merge.id}>
              <span className="merge-history__pair">
                <strong>{merge.target_email || `User #${merge.target_user_id}`}</strong>
                <span className="merge-history__absorbed">
                  absorbed {merge.source_email || `User #${merge.source_user_id}`}
                </span>
                <span className="merge-history__meta">
                  {formatDate(merge.created_at)}
                  {merge.merged_by_email && ` · by ${merge.merged_by_email}`}
                  {moved(merge.merge_details) && ` · ${moved(merge.merge_details)}`}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </DSCard>
  );
}

// Only what actually moved. The old table always printed "0 credits, 0
// payments", which is the commonest case and says nothing.
function moved(details) {
  if (!details || typeof details !== 'object' || !details.recordsMoved) return '';
  const labels = {
    credits: 'credit rows',
    membershipPayments: 'payments',
    squareCustomers: 'Square customers',
    squareSubscriptions: 'Square subscriptions',
    squarePayments: 'Square payments',
    externalLinks: 'external links',
    eventHosts: 'host assignments'
  };
  const parts = Object.entries(details.recordsMoved)
    .filter(([, count]) => Number(count) > 0)
    .map(([key, count]) => `${count} ${labels[key] || key}`);
  return parts.length > 0 ? `moved ${parts.join(', ')}` : 'nothing to move';
}

function formatDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default MergeHistory;
export { moved };
